import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  confirmAttachment,
  generateUploadUrl,
  putToGcs,
  type AttachmentAuthMode,
  type ConfirmedAttachment,
} from "@/services/taskerAttachments";

export type UploadStatus =
  | "idle"
  | "generating"
  | "uploading"
  | "confirming"
  | "done"
  | "error";

export type UploadResult = {
  attachment_id: string;
  file_name: string;
};

export type UseAttachmentUploadResult = {
  status: UploadStatus;
  error: string | null;
  result: UploadResult | null;
  upload: (
    file: File,
    ownerRef?: string,
    ownerType?: string,
    authMode?: AttachmentAuthMode,
  ) => Promise<UploadResult | null>;
  reset: () => void;
  cancel: () => void;
};

function messageOf(err: unknown, fallback: string): string {
  if (err && typeof err === "object") {
    const e = err as {
      response?: { data?: { message?: string; error?: string } };
      message?: string;
    };
    return (
      e.response?.data?.message ??
      e.response?.data?.error ??
      e.message ??
      fallback
    );
  }
  return fallback;
}

function isSignedUrlExpired(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
}

export function useAttachmentUpload(): UseAttachmentUploadResult {
  const [status, setStatus] = useState<UploadStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  const inFlight = useRef(false);
  // Generation guard — stale in-flight uploads can't overwrite a subsequent Remove/Replace.
  const generation = useRef(0);
  const activeAbort = useRef<AbortController | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      activeAbort.current?.abort();
    };
  }, []);

  const safeSetStatus = useCallback((s: UploadStatus) => {
    if (isMounted.current) setStatus(s);
  }, []);
  const safeSetError = useCallback((e: string | null) => {
    if (isMounted.current) setError(e);
  }, []);
  const safeSetResult = useCallback((r: UploadResult | null) => {
    if (isMounted.current) setResult(r);
  }, []);

  const reset = useCallback(() => {
    safeSetStatus("idle");
    safeSetError(null);
    safeSetResult(null);
  }, [safeSetStatus, safeSetError, safeSetResult]);

  const cancel = useCallback(() => {
    activeAbort.current?.abort();
    activeAbort.current = null;
    generation.current += 1;
    inFlight.current = false;
    reset();
  }, [reset]);

  const upload = useCallback(
    async (
      file: File,
      ownerRef?: string,
      ownerType?: string,
      authMode: AttachmentAuthMode = "vendor",
    ): Promise<UploadResult | null> => {
      if (inFlight.current) return null;
      inFlight.current = true;
      generation.current += 1;
      const myGen = generation.current;
      const ac = new AbortController();
      activeAbort.current = ac;

      safeSetError(null);
      safeSetResult(null);

      const doUpload = async (): Promise<UploadResult> => {
        safeSetStatus("generating");
        let slot = await generateUploadUrl(file.name, authMode);

        safeSetStatus("uploading");
        try {
          await putToGcs(slot.url, file, ac.signal);
        } catch (err) {
          if (axios.isCancel(err)) throw err;
          // Signed URL expired mid-upload — regenerate once and retry.
          if (isSignedUrlExpired(err)) {
            slot = await generateUploadUrl(file.name, authMode);
            await putToGcs(slot.url, file, ac.signal);
          } else {
            throw err;
          }
        }

        safeSetStatus("confirming");
        const confirmed: ConfirmedAttachment = await confirmAttachment(
          {
            attachment_id: slot.attachment_id,
            file_name: file.name,
            req_id: ownerRef,
            type: ownerType,
          },
          authMode,
        );
        return {
          attachment_id: confirmed.attachment_id,
          file_name: confirmed.name || file.name,
        };
      };

      try {
        const uploaded = await doUpload();
        if (myGen !== generation.current) return null;
        safeSetResult(uploaded);
        safeSetStatus("done");
        return uploaded;
      } catch (err) {
        if (myGen !== generation.current) return null;
        if (axios.isCancel(err)) {
          safeSetStatus("idle");
          return null;
        }
        safeSetError(messageOf(err, "Upload failed"));
        safeSetStatus("error");
        return null;
      } finally {
        if (myGen === generation.current) {
          inFlight.current = false;
          activeAbort.current = null;
        }
      }
    },
    [safeSetStatus, safeSetError, safeSetResult],
  );

  return { status, error, result, upload, reset, cancel };
}
